import AddIcon from '@mui/icons-material/Add';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import ListFilterEmptyState from 'src/common/components/dashboard/ListFilterEmptyState';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import Empty from 'src/common/components/Empty';
import { ticketCardGridItemSx, ticketCardGridSize, ticketCardScrollContainerSx } from 'src/common/styles/ticketCardStyles';
import { filterAndSortSponsors } from 'src/helpers/filterSponsors';
import useDeferredFileUsage from 'src/hooks/useDeferredFileUsage';
import usePersistentView from 'src/hooks/usePersistentView';
import { UploadedFile } from 'src/interfaces/FileManager';
import { IBaseSponsor, ISponsor } from 'src/interfaces/Sponsor';
import { EView } from 'src/interfaces/View';
import useSponsorsStore from 'src/stores/sponsorsStore';
import SponsorsManagerCard from './components/SponsorsManagerCard';
import SponsorsTable from './components/SponsorsTable';
import SponsorsEditorDialog from './SponsorsEditorDialog';
import SponsorsToolbar from './components/SponsorsToolbar';

const emptySponsor = (): IBaseSponsor => ({
  name: '',
  url: '',
  imageUrl: '',
  alt: { en: '', hu: '', ro: '' },
  published: true,
  isOrganizer: false,
  sortOrder: 0,
});

export default function SponsorsManager() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const {
    sponsors,
    isLoading,
    search,
    publishedFilter,
    sortBy,
    sortOrder,
    saveSponsor,
    deleteSponsor,
    fetchSponsors,
  } = useSponsorsStore(s => s);
  const [view, setView] = usePersistentView();

  const filteredSponsors = useMemo(
    () => filterAndSortSponsors(sponsors, { search, publishedFilter, sortBy, sortOrder }),
    [sponsors, search, publishedFilter, sortBy, sortOrder],
  );
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<IBaseSponsor>(emptySponsor());
  const [editingId, setEditingId] = useState<string | undefined>();

  const { addPendingFile, markPendingFilesAsUsed, clearPendingFiles } = useDeferredFileUsage({
    contentType: 'sponsor',
    contentId: editingId ?? 'new',
    fieldName: 'logo',
  });

  useEffect(() => {
    if (!open) {
      clearPendingFiles();
    }
  }, [open, clearPendingFiles]);

  const openNew = () => {
    setForm(emptySponsor());
    setEditingId(undefined);
    setOpen(true);
  };

  const openEdit = (sponsor: ISponsor) => {
    setForm(sponsor);
    setEditingId(sponsor.id);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    clearPendingFiles();
    setOpen(false);
  };

  const handleImageSelect = (file: UploadedFile) => {
    if (file.uploadEndpoint !== 'image') return;
    setForm(prev => ({
      ...prev,
      imageUrl: file.fileUrl,
      imageFileId: file.id,
    }));
    addPendingFile(file.id);
  };

  const handleRemoveImage = () => {
    setForm(prev => ({
      ...prev,
      imageUrl: '',
      imageFileId: undefined,
    }));
  };

  const handleSave = async () => {
    if (!form.imageUrl?.trim()) {
      enqueueSnackbar(t('system.sponsors.logoRequired'), { variant: 'error' });
      return;
    }
    await saveSponsor(form, editingId);
    await markPendingFilesAsUsed();
    await fetchSponsors();
    setOpen(false);
    enqueueSnackbar(t('system.pages.saveSuccess'), { variant: 'success' });
  };

  const handleDelete = async (id: string) => {
    const { confirmed } = await confirm({ title: t('system.sponsors.deleteConfirm') });
    if (!confirmed) {
      return;
    }
    await deleteSponsor(id);
    await fetchSponsors();
  };

  return (
    <>
      <DashboardListPageLayout
        title={t('system.sponsors.pageTitle')}
        isLoading={isLoading}
        headerActions={
          <Button
            {...dashboardPrimaryActionButtonProps()}
            startIcon={<AddIcon />}
            onClick={openNew}
          >
            {t('system.sponsors.add')}
          </Button>
        }
        toolbar={<SponsorsToolbar view={view} onSetView={setView} />}
      >
        {!isLoading && sponsors.length === 0 && (
          <Empty
            icon={<ExtensionOutlinedIcon />}
            title={t('empty.sponsors.title')}
            description={t('empty.sponsors.description')}
            primaryAction={{
              label: t('system.sponsors.add'),
              onClick: openNew,
            }}
          />
        )}
        {!isLoading && sponsors.length > 0 && filteredSponsors.length === 0 && (
          <ListFilterEmptyState />
        )}
        {filteredSponsors.length > 0 && (
          <Box sx={ticketCardScrollContainerSx(view !== EView.table)}>
            {view === EView.grid && (
              <Grid container spacing={2} sx={{ overflow: 'visible' }}>
                {filteredSponsors.map(sponsor => (
                  <Grid size={ticketCardGridSize} key={sponsor.id} sx={ticketCardGridItemSx}>
                    <SponsorsManagerCard
                      sponsor={sponsor}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            {view === EView.table && (
              <SponsorsTable
                sponsors={filteredSponsors}
                onEdit={openEdit}
                onDelete={handleDelete}
              />
            )}
          </Box>
        )}
      </DashboardListPageLayout>
      <SponsorsEditorDialog
        open={open}
        onClose={handleCloseDialog}
        editingId={editingId}
        form={form}
        onFormChange={setForm}
        onSave={handleSave}
        onImageSelect={handleImageSelect}
        onRemoveImage={handleRemoveImage}
      />
    </>
  );
}
